/**
 * Formation positions in normalized 0-1 coordinates.
 * For vertical (mobile): x=horizontal, y=vertical (0=top, 1=bottom)
 * For horizontal (desktop): coordinates are swapped: x becomes depth, y becomes width
 *
 * Team 1 occupies the first half (y: 0.05-0.45 vertical, x: 0.05-0.45 horizontal)
 * Team 2 occupies the second half (mirrored)
 */

export interface FormationPosition {
  x: number;
  y: number;
  role: string; // ARQ, DEF, MC, DEL
}

export interface Formation {
  name: string;
  positions: FormationPosition[];
}

// Formations for team 1 (top/left half) — team 2 is mirrored
const FORMATIONS: Record<number, Formation[]> = {
  5: [
    {
      name: '2-1-1',
      positions: [
        { x: 0.5, y: 0.05, role: 'ARQ' },
        { x: 0.25, y: 0.18, role: 'DEF' },
        { x: 0.75, y: 0.18, role: 'DEF' },
        { x: 0.5, y: 0.30, role: 'MC' },
        { x: 0.5, y: 0.42, role: 'DEL' },
      ],
    },
    {
      name: '1-2-1',
      positions: [
        { x: 0.5, y: 0.05, role: 'ARQ' },
        { x: 0.5, y: 0.16, role: 'DEF' },
        { x: 0.25, y: 0.30, role: 'MC' },
        { x: 0.75, y: 0.30, role: 'MC' },
        { x: 0.5, y: 0.42, role: 'DEL' },
      ],
    },
  ],
  6: [
    {
      name: '2-2-1',
      positions: [
        { x: 0.5, y: 0.05, role: 'ARQ' },
        { x: 0.25, y: 0.16, role: 'DEF' },
        { x: 0.75, y: 0.16, role: 'DEF' },
        { x: 0.25, y: 0.30, role: 'MC' },
        { x: 0.75, y: 0.30, role: 'MC' },
        { x: 0.5, y: 0.42, role: 'DEL' },
      ],
    },
  ],
  7: [
    {
      name: '2-3-1',
      positions: [
        { x: 0.5, y: 0.05, role: 'ARQ' },
        { x: 0.25, y: 0.14, role: 'DEF' },
        { x: 0.75, y: 0.14, role: 'DEF' },
        { x: 0.2, y: 0.28, role: 'MC' },
        { x: 0.5, y: 0.28, role: 'MC' },
        { x: 0.8, y: 0.28, role: 'MC' },
        { x: 0.5, y: 0.42, role: 'DEL' },
      ],
    },
    {
      name: '3-2-1',
      positions: [
        { x: 0.5, y: 0.05, role: 'ARQ' },
        { x: 0.2, y: 0.14, role: 'DEF' },
        { x: 0.5, y: 0.14, role: 'DEF' },
        { x: 0.8, y: 0.14, role: 'DEF' },
        { x: 0.3, y: 0.28, role: 'MC' },
        { x: 0.7, y: 0.28, role: 'MC' },
        { x: 0.5, y: 0.42, role: 'DEL' },
      ],
    },
  ],
  8: [
    {
      name: '3-3-1',
      positions: [
        { x: 0.5, y: 0.05, role: 'ARQ' },
        { x: 0.2, y: 0.13, role: 'DEF' },
        { x: 0.5, y: 0.13, role: 'DEF' },
        { x: 0.8, y: 0.13, role: 'DEF' },
        { x: 0.2, y: 0.27, role: 'MC' },
        { x: 0.5, y: 0.27, role: 'MC' },
        { x: 0.8, y: 0.27, role: 'MC' },
        { x: 0.5, y: 0.40, role: 'DEL' },
      ],
    },
  ],
  9: [
    {
      name: '3-3-2',
      positions: [
        { x: 0.5, y: 0.05, role: 'ARQ' },
        { x: 0.2, y: 0.13, role: 'DEF' },
        { x: 0.5, y: 0.13, role: 'DEF' },
        { x: 0.8, y: 0.13, role: 'DEF' },
        { x: 0.2, y: 0.26, role: 'MC' },
        { x: 0.5, y: 0.26, role: 'MC' },
        { x: 0.8, y: 0.26, role: 'MC' },
        { x: 0.35, y: 0.39, role: 'DEL' },
        { x: 0.65, y: 0.39, role: 'DEL' },
      ],
    },
  ],
  10: [
    {
      name: '3-4-2',
      positions: [
        { x: 0.5, y: 0.05, role: 'ARQ' },
        { x: 0.2, y: 0.12, role: 'DEF' },
        { x: 0.5, y: 0.12, role: 'DEF' },
        { x: 0.8, y: 0.12, role: 'DEF' },
        { x: 0.15, y: 0.25, role: 'MC' },
        { x: 0.4, y: 0.25, role: 'MC' },
        { x: 0.6, y: 0.25, role: 'MC' },
        { x: 0.85, y: 0.25, role: 'MC' },
        { x: 0.35, y: 0.38, role: 'DEL' },
        { x: 0.65, y: 0.38, role: 'DEL' },
      ],
    },
  ],
  11: [
    {
      name: '4-4-2',
      positions: [
        { x: 0.5, y: 0.05, role: 'ARQ' },
        { x: 0.15, y: 0.12, role: 'DEF' },
        { x: 0.38, y: 0.12, role: 'DEF' },
        { x: 0.62, y: 0.12, role: 'DEF' },
        { x: 0.85, y: 0.12, role: 'DEF' },
        { x: 0.15, y: 0.25, role: 'MC' },
        { x: 0.38, y: 0.25, role: 'MC' },
        { x: 0.62, y: 0.25, role: 'MC' },
        { x: 0.85, y: 0.25, role: 'MC' },
        { x: 0.35, y: 0.38, role: 'DEL' },
        { x: 0.65, y: 0.38, role: 'DEL' },
      ],
    },
    {
      name: '4-3-3',
      positions: [
        { x: 0.5, y: 0.05, role: 'ARQ' },
        { x: 0.15, y: 0.12, role: 'DEF' },
        { x: 0.38, y: 0.12, role: 'DEF' },
        { x: 0.62, y: 0.12, role: 'DEF' },
        { x: 0.85, y: 0.12, role: 'DEF' },
        { x: 0.25, y: 0.25, role: 'MC' },
        { x: 0.5, y: 0.25, role: 'MC' },
        { x: 0.75, y: 0.25, role: 'MC' },
        { x: 0.2, y: 0.38, role: 'DEL' },
        { x: 0.5, y: 0.38, role: 'DEL' },
        { x: 0.8, y: 0.38, role: 'DEL' },
      ],
    },
  ],
};

/**
 * Get formation positions for a team.
 * @param playerCount - number of players per team
 * @param formationIndex - which formation variant (0, 1, 2...)
 * @param isTeam2 - if true, mirrors positions for the second team
 * @param isVertical - if true, uses vertical layout (mobile), otherwise horizontal (desktop)
 */
export function getFormationPositions(
  playerCount: number,
  formationIndex: number = 0,
  isTeam2: boolean = false,
  isVertical: boolean = true
): FormationPosition[] {
  const count = Math.min(Math.max(playerCount, 5), 11);
  const formations = FORMATIONS[count] || FORMATIONS[5];
  const formation = formations[formationIndex % formations.length];

  return formation.positions.map(pos => {
    let x = pos.x;
    let y = pos.y;

    if (isTeam2) {
      // Mirror: team 2 occupies the opposite half
      if (isVertical) {
        y = 1 - y; // flip vertically
      } else {
        x = 1 - x; // flip horizontally
      }
    } else if (!isVertical) {
      // Desktop: swap x/y for horizontal layout (team 1 on left)
      const temp = x;
      x = y; // depth becomes horizontal
      y = temp; // width becomes vertical
    }

    if (isTeam2 && !isVertical) {
      // Desktop team 2: swap then mirror
      const temp = pos.x;
      x = 1 - pos.y;
      y = temp;
    }

    return { x, y, role: pos.role };
  });
}

export function getFormationCount(playerCount: number): number {
  const count = Math.min(Math.max(playerCount, 5), 11);
  return (FORMATIONS[count] || FORMATIONS[5]).length;
}
